import { useState, useMemo } from "react";
import {
  Phone,
  PhoneCall,
  CheckCircle,
  XCircle,
  Clock,
  HelpCircle,
  RotateCw,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { MetricCards } from "@/components/metric-cards";
import { LoadMoreButton } from "@/components/load-more-button";
import { CallDetailSheet } from "@/components/call-detail-sheet";
import { OpenDayCallsButton } from "@/components/day-calls-drawer";
import { DataTable } from "@/components/data-table";
import { SearchInput } from "@/components/search-input";
import { callColumns } from "./calls-columns";
import { useDashboardStats, useDashboardCases } from "@/hooks/use-dashboard";
import { useDebounce } from "@/hooks/use-debounce";
import { flattenCasesToCallRows, filterCallRows, filterCallRowsBySearch } from "@/lib/data-transform";
import { cn } from "@/utils";
import type { CallRow, CaseFilter } from "@/types";

const TABS: { value: CaseFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "successful", label: "Successful" },
  { value: "failed", label: "Failed" },
  { value: "pending_verification", label: "Pending Verification" },
];

const DEFAULT_SORT = [{ id: "timestamp", desc: true }];

export default function CallsPage() {
  const [filter, setFilter] = useState<CaseFilter>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCall, setSelectedCall] = useState<CallRow | null>(null);

  const debouncedSearch = useDebounce(searchTerm);

  const { stats, loading: statsLoading, refresh: refreshStats } = useDashboardStats();
  const {
    cases,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    refresh: refreshCases,
  } = useDashboardCases(filter);

  const callRows = useMemo(
    () => filterCallRowsBySearch(
      filterCallRows(flattenCasesToCallRows(cases), filter),
      debouncedSearch
    ),
    [cases, filter, debouncedSearch]
  );

  const metrics = useMemo(
    () => [
      { label: "Total", value: stats?.total ?? 0, icon: Phone, color: "text-blue-500" },
      { label: "Successful", value: stats?.successful ?? 0, icon: CheckCircle, color: "text-green-500" },
      {
        label: "Failed",
        value: stats?.failed ?? 0,
        valueCaption: "retryable",
        secondary: { value: stats?.failed_locked ?? 0, label: "locked" },
        icon: XCircle,
        color: "text-red-500",
      },
      { label: "Active", value: stats?.active ?? 0, icon: PhoneCall, color: "text-yellow-500" },
      { label: "Pending", value: stats?.pending ?? 0, icon: Clock, color: "text-purple-500" },
      {
        label: "Other",
        value: stats?.other ?? 0,
        icon: HelpCircle,
        color: "text-slate-500",
      },
    ],
    [stats]
  );

  const handleRefresh = () => {
    refreshStats();
    refreshCases();
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Last Calls</h1>
          <p className="text-sm text-muted-foreground">
            Showing {callRows.length} calls from {cases.length} cases
            {stats?.total ? ` (${stats.total} total)` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <OpenDayCallsButton />
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={loading || statsLoading}
            aria-label="Refresh calls"
          >
            <RotateCw className={cn("h-4 w-4", (loading || statsLoading) && "animate-spin")} />
          </Button>
        </div>
      </div>

      <MetricCards metrics={metrics} loading={statsLoading} />

      <div className="flex gap-3 flex-wrap items-center">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by Case ID, Call ID, Name, Member ID, Phone..."
        />
        <Tabs value={filter} onValueChange={(v) => setFilter(v as CaseFilter)}>
          <TabsList>
            {TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <DataTable
        columns={callColumns}
        data={callRows}
        loading={loading}
        defaultSort={DEFAULT_SORT}
        emptyMessage="No calls found."
        onRowClick={setSelectedCall}
      />

      <LoadMoreButton
        hasMore={hasMore}
        loading={loadingMore}
        loadedCount={cases.length}
        totalCount={stats?.total}
        onClick={loadMore}
      />

      <CallDetailSheet
        callRow={selectedCall}
        onClose={() => setSelectedCall(null)}
      />
    </div>
  );
}
